/**********************************************************************************************************
 *   BASE IMPORTS
 **********************************************************************************************************/
import { RefObject } from 'react'
import { Field, Form, Formik, FormikProps } from 'formik'
import * as Yup from 'yup'

/**********************************************************************************************************
 *   SHARED IMPORTS
 **********************************************************************************************************/
import { Form as NXUIForm } from '@nexigendigital/nxui'
import { useUpdatePasswordMutation } from 'api/account'
import { ChangePassword } from 'models/account'
import { regex } from 'helpers/utils'

/**********************************************************************************************************
 *   INTERFACE
 **********************************************************************************************************/
interface Props {
    form: RefObject<FormikProps<ChangePassword>>
}

/**********************************************************************************************************
 *   COMPONENT START
 **********************************************************************************************************/
export default function ChangePasswordForm({ form }: Props) {
    const [updatePassword] = useUpdatePasswordMutation({ fixedCacheKey: 'change-password' })

    return (
        <Formik
            innerRef={form}
            initialValues={{
                old_password: '',
                password: '',
                password_confirmation: ''
            }}
            validationSchema={
                Yup.object({
                    old_password: Yup.string()
                        .required('Required'),
                    password: Yup.string()
                        .required('Required')
                        .matches(
                            regex.password,
                            'Password must contain at least 8 characters, one uppercase, one number and one special case character'
                        ),
                    password_confirmation: Yup.string()
                        .oneOf([Yup.ref('password'), null], 'Password must match')
                        .required('Required')
                })
            }
            onSubmit={({ old_password, password, password_confirmation }) => {
                updatePassword({ old_password, password, password_confirmation: password_confirmation })
            }}
        >
            {({ isSubmitting }) => (
                <Form id={'changePasswordForm'}>
                    <Field
                        id={'old_password'}
                        label={'Current Password'}
                        name={'old_password'}
                        type={'password'}
                        autoComplete={'current-password'}
                        disabled={isSubmitting}
                        component={NXUIForm.PasswordField}
                    />
                    <Field
                        id={'password'}
                        label={'New Password'}
                        name={'password'}
                        type={'password'}
                        autoComplete={'new-password'}
                        disabled={isSubmitting}
                        component={NXUIForm.PasswordField}
                    />
                    <Field
                        id={'password_confirmation'}
                        label={'Confirm New Password'}
                        name={'password_confirmation'}
                        type={'password'}
                        autoComplete={'new-password'}
                        disabled={isSubmitting}
                        component={NXUIForm.PasswordField}
                    />
                </Form>
            )}
        </Formik>
    )
}
